"use client";

import React, { useState, useCallback, useMemo } from "react";
import {
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Flex,
  Tooltip,
  IconButton,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { Plus, LayoutGrid, Pencil, Trash2 } from "lucide-react";
import { HiOutlinePhoneMissedCall } from "react-icons/hi";
import { VscCallOutgoing } from "react-icons/vsc";
import { TbCalendarSearch, TbCalendarUp } from "react-icons/tb";
import { IoKeypadOutline } from "react-icons/io5";
import type { FunctionConfig } from "../../../voice-services/agentService";
import { EndCallModal } from "./EndCallModal";
import { TransferCallModal } from "./TransferCallModal";
import { CalendarAvailabilityModal } from "./CalendarAvailabilityModal";
import { BookAppointmentModal } from "./BookAppointmentModal";
import { PressDigitModal } from "./PressDigitModal";
import { CustomFunctionModal } from "./CustomFunctionModal";
import { ChevronDownIcon } from "@chakra-ui/icons";

// Define function types
type FunctionType =
  | "end_call"
  | "transfer_call"
  | "check_availability_cal"
  | "book_appointment_cal"
  | "press_digit"
  | "custom";

interface AgentFunctionsProps {
  agentId: string;
  llmId: string;
  functions: FunctionConfig[];
  onUpdate: (
    updatedFunctions: FunctionConfig[]
  ) => Promise<{ success: boolean; error?: string }>;
  isLoading: boolean;
}

export const AgentFunctions: React.FC<AgentFunctionsProps> = React.memo(
  ({ agentId, llmId, functions, onUpdate, isLoading }) => {
    const [selectedFunction, setSelectedFunction] =
      useState<FunctionType | null>(null);
    const [editingFunction, setEditingFunction] = useState<{
      index: number;
      config: FunctionConfig;
    } | null>(null);

    const handleAddFunction = useCallback((type: FunctionType) => {
      setSelectedFunction(type);
      setEditingFunction(null);
    }, []);

    const handleEditFunction = useCallback(
      (index: number) => {
        const functionConfig = functions[index];
        setEditingFunction({ index, config: functionConfig });
        setSelectedFunction(functionConfig.type as FunctionType);
      },
      [functions]
    );

    const handleSaveFunction = useCallback(
      async (
        newFunction: FunctionConfig
      ): Promise<{ success: boolean; error?: string }> => {
        let updatedFunctions: FunctionConfig[];
        const previousFunctions = [...functions];

        if (editingFunction !== null) {
          updatedFunctions = functions.map((f, i) =>
            i === editingFunction.index ? newFunction : f
          );
        } else {
          updatedFunctions = [...functions, newFunction];
        }

        const result = await onUpdate(updatedFunctions);

        if (result.success) {
          setSelectedFunction(null);
          setEditingFunction(null);
        }

        return result;
      },
      [functions, onUpdate, editingFunction]
    );

    const handleRemoveFunction = useCallback(
      (index: number) => {
        const updatedFunctions = functions.filter((_, i) => i !== index);
        onUpdate(updatedFunctions);
      },
      [functions, onUpdate]
    );

    const getFunctionIcon = useCallback((type: FunctionType) => {
      switch (type) {
        case "end_call":
          return <HiOutlinePhoneMissedCall size={16} />;
        case "transfer_call":
          return <VscCallOutgoing size={16} />;
        case "check_availability_cal":
          return <TbCalendarSearch size={16} />;
        case "book_appointment_cal":
          return <TbCalendarUp size={16} />;
        case "press_digit":
          return <IoKeypadOutline size={16} />;
        case "custom":
          return (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M4.33337 14.6667V10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4.33337 3.33398V1.33398"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11.6666 14.666V12.666"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11.6666 6.00065V1.33398"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6.33337 4.66732V8.66732C6.33337 9.40065 6.00004 10.0007 5.00004 10.0007H3.66671C2.66671 10.0007 2.33337 9.40065 2.33337 8.66732V4.66732C2.33337 3.93398 2.66671 3.33398 3.66671 3.33398H5.00004C6.00004 3.33398 6.33337 3.93398 6.33337 4.66732Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.6666 7.33333V11.3333C13.6666 12.0667 13.3333 12.6667 12.3333 12.6667H11C9.99996 12.6667 9.66663 12.0667 9.66663 11.3333V7.33333C9.66663 6.6 9.99996 6 11 6H12.3333C13.3333 6 13.6666 6.6 13.6666 7.33333Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          );
        default:
          return <LayoutGrid size={16} />;
      }
    }, []);

    const functionsList = useMemo(
      () => (
        <VStack spacing={2} align="stretch">
          {functions.map((func, index) => (
            <Flex
              key={index}
              justify="space-between"
              align="center"
              p={1.5}
              bg="gray.50"
              borderRadius="md"
              minHeight="32px"
            >
              <HStack spacing={2} flex={1} overflow="hidden">
                {getFunctionIcon(func.type as FunctionType)}
                <Text fontSize="xs" isTruncated color="gray.800">
                  {func.name}
                </Text>
              </HStack>
              <HStack spacing={1} flexShrink={0}>
                <Tooltip label="Edit">
                  <IconButton
                    aria-label="Edit"
                    icon={<Pencil size={12} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => handleEditFunction(index)}
                    isDisabled={isLoading}
                    color="gray.700"
                    _hover={{ bg: "primary.50", color: "primary.700" }}
                    _active={{ bg: "primary.100" }}
                    _focusVisible={{
                      boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
                    }}
                  />
                </Tooltip>
                <Tooltip label="Remove">
                  <IconButton
                    aria-label="Remove"
                    icon={<Trash2 size={12} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => handleRemoveFunction(index)}
                    isDisabled={isLoading}
                    color="gray.700"
                    _hover={{ bg: "primary.50", color: "primary.700" }}
                    _active={{ bg: "primary.100" }}
                    _focusVisible={{
                      boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
                    }}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          ))}
        </VStack>
      ),
      [
        functions,
        handleEditFunction,
        handleRemoveFunction,
        getFunctionIcon,
        isLoading,
      ]
    );

    // Options for the custom select with icons.
    interface Option {
      value: string;
      label: string;
      icon: React.ReactElement;
    }
    const selectOptions: Option[] = useMemo(
      () => [
        {
          value: "end_call",
          label: "End Call",
          icon: <HiOutlinePhoneMissedCall size={16} />,
        },
        {
          value: "transfer_call",
          label: "Call Transfer",
          icon: <VscCallOutgoing size={16} />,
        },
        {
          value: "check_availability_cal",
          label: "Check Calendar Availability (Cal.com)",
          icon: <TbCalendarSearch size={16} />,
        },
        {
          value: "book_appointment_cal",
          label: "Book on the Calendar (Cal.com)",
          icon: <TbCalendarUp size={16} />,
        },
        {
          value: "press_digit",
          label: "Press Digit (IVR Navigation)",
          icon: <IoKeypadOutline size={16} />,
        },
        {
          value: "custom",
          label: "Custom Function",
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M4.33337 14.6667V10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4.33337 3.33398V1.33398"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11.6666 14.666V12.666"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11.6666 6.00065V1.33398"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6.33337 4.66732V8.66732C6.33337 9.40065 6.00004 10.0007 5.00004 10.0007H3.66671C2.66671 10.0007 2.33337 9.40065 2.33337 8.66732V4.66732C2.33337 3.93398 2.66671 3.33398 3.66671 3.33398H5.00004C6.00004 3.33398 6.33337 3.93398 6.33337 4.66732Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.6666 7.33333V11.3333C13.6666 12.0667 13.3333 12.6667 12.3333 12.6667H11C9.99996 12.6667 9.66663 12.0667 9.66663 11.3333V7.33333C9.66663 6.6 9.99996 6 11 6H12.3333C13.3333 6 13.6666 6.6 13.6666 7.33333Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit={10}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ),
        },
      ],
      []
    );

    return (
      <AccordionItem border="none">
        <h2>
          <AccordionButton
            py={3}
            px={4}
            bg="transparent"
            _hover={{ bg: "primary.50" }}
            _active={{ bg: "primary.100" }}
            _focusVisible={{
              boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
            }}
          >
            <Box
              flex="1"
              textAlign="left"
              fontSize="sm"
              display="flex"
              alignItems="center"
              gap={2}
              color="primary.600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M3.25 3.25H9.25V9.25H3.25V3.25ZM3.25 10.75H9.25V16.75H3.25V10.75ZM10.75 3.25H16.75V9.25H10.75V3.25ZM10.75 10.75H16.75V16.75H10.75V10.75ZM12.25 4.75V7.75H15.25V4.75H12.25ZM12.25 12.25V15.25H15.25V12.25H12.25ZM4.75 4.75V7.75H7.75V4.75H4.75ZM4.75 12.25V15.25H7.75V12.25H4.75Z"
                  fill="currentColor"
                />
              </svg>
              Functions
            </Box>
            <AccordionIcon color="primary.600" />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4} bg="gray.100">
          <VStack spacing={3} align="stretch">
            <Text fontSize="xs" color="gray.600">
              Enable your agent with capabilities such as calendar bookings,
              call termination, etc.
            </Text>
            {isLoading ? (
              <Spinner size="sm" color="primary.500" />
            ) : (
              <>
                {functionsList}
                <Box>
                  <CustomSelectWithIcons
                    options={selectOptions}
                    onSelect={(value) => {
                      if (value) {
                        handleAddFunction(value as FunctionType);
                      }
                    }}
                    isDisabled={isLoading}
                  />
                </Box>
              </>
            )}
          </VStack>

          {/* Render modals based on selected function */}
          <EndCallModal
            key={
              selectedFunction === "end_call"
                ? `end-call-${editingFunction?.index ?? "new"}`
                : "end-call-closed"
            }
            isOpen={selectedFunction === "end_call"}
            onClose={() => {
              setSelectedFunction(null);
              setEditingFunction(null);
            }}
            onSave={handleSaveFunction}
            initialValues={editingFunction?.config}
          />
          <TransferCallModal
            key={
              selectedFunction === "transfer_call"
                ? `transfer-call-${editingFunction?.index ?? "new"}`
                : "transfer-call-closed"
            }
            isOpen={selectedFunction === "transfer_call"}
            onClose={() => {
              setSelectedFunction(null);
              setEditingFunction(null);
            }}
            onSave={handleSaveFunction}
            initialValues={editingFunction?.config}
          />
          <CalendarAvailabilityModal
            key={
              selectedFunction === "check_availability_cal"
                ? `check-cal-${editingFunction?.index ?? "new"}`
                : "check-cal-closed"
            }
            isOpen={selectedFunction === "check_availability_cal"}
            onClose={() => {
              setSelectedFunction(null);
              setEditingFunction(null);
            }}
            onSave={handleSaveFunction}
            initialValues={editingFunction?.config}
          />
          <BookAppointmentModal
            key={
              selectedFunction === "book_appointment_cal"
                ? `book-cal-${editingFunction?.index ?? "new"}`
                : "book-cal-closed"
            }
            isOpen={selectedFunction === "book_appointment_cal"}
            onClose={() => {
              setSelectedFunction(null);
              setEditingFunction(null);
            }}
            onSave={handleSaveFunction}
            initialValues={editingFunction?.config}
          />
          <PressDigitModal
            key={
              selectedFunction === "press_digit"
                ? `press-digit-${editingFunction?.index ?? "new"}`
                : "press-digit-closed"
            }
            isOpen={selectedFunction === "press_digit"}
            onClose={() => {
              setSelectedFunction(null);
              setEditingFunction(null);
            }}
            onSave={handleSaveFunction}
            initialValues={editingFunction?.config}
          />
          <CustomFunctionModal
            key={
              selectedFunction === "custom"
                ? `custom-${editingFunction?.index ?? "new"}`
                : "custom-closed"
            }
            isOpen={selectedFunction === "custom"}
            onClose={() => {
              setSelectedFunction(null);
              setEditingFunction(null);
            }}
            onSave={handleSaveFunction}
            initialValues={editingFunction?.config}
          />
        </AccordionPanel>
      </AccordionItem>
    );
  }
);

// ─────────────────────────────────────────────────────────────
// CustomSelectWithIcons
// ─────────────────────────────────────────────────────────────
interface Option {
  value: string;
  label: string;
  icon: React.ReactElement;
}

interface CustomSelectWithIconsProps {
  label?: string;
  options: Option[];
  onSelect: (value: string) => void;
  isDisabled?: boolean;
}

const CustomSelectWithIcons: React.FC<CustomSelectWithIconsProps> = ({
  label = "Add a function",
  options,
  onSelect,
  isDisabled,
}) => {
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  return (
    <Box>
      <Text mb={2} fontWeight="medium" color="gray.800">
        {label}
      </Text>
      <Menu>
        <MenuButton
          as={Button}
          ref={buttonRef}
          leftIcon={<Plus size={14} />}
          rightIcon={<ChevronDownIcon />}
          size="xs"
          variant="outline"
          colorScheme="primary"
          bg="transparent"
          color="gray.800"
          borderColor="primary.300"
          _hover={{ bg: "primary.50", borderColor: "primary.400" }}
          _active={{ bg: "primary.100", borderColor: "primary.500" }}
          _expanded={{ bg: "white", borderColor: "primary.500" }}
          _focusVisible={{
            borderColor: "primary.500",
            boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
          }}
          width="100%"
          isDisabled={isDisabled}
        >
          Add
        </MenuButton>
        <MenuList
          bg="gray.100"
          borderColor="primary.200"
          overflowY="auto"
          minW={
            buttonRef.current ? `${buttonRef.current.offsetWidth}px` : "100%"
          }
          sx={{
            scrollbarWidth: "thin",
            scrollbarColor: "var(--chakra-colors-primary-400) transparent",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "var(--chakra-colors-primary-400)",
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "var(--chakra-colors-primary-500)",
            },
          }}
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => onSelect(option.value)}
              icon={option.icon}
              bg="gray.100"
              color="gray.800"
              fontSize="sm"
              _hover={{ bg: "primary.50" }}
              _focus={{ bg: "primary.50" }}
              _active={{ bg: "primary.100" }}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};

export default AgentFunctions;
